import { usePersistedState } from "@/lib/persistedFilters";
// UC CDR → Alerts. Every domain judged against its own normal week, so a small
// account tripling its minutes shows up next to a big one doing the same.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Check, Loader2, RotateCcw, Search, Settings2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Pill, clock } from "./ui";

const STATES = [["open", "Open"], ["acked", "Acknowledged"], ["all", "All"]];

export default function Alerts({ onCount }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ open: 0, settings: {}, last_run_at: "", history: {} });
  const [status, setStatus] = usePersistedState("uccdr-alerts.status", "open");
  const [q, setQ] = usePersistedState("uccdr-alerts.q", "");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/uc-cdr/alerts", { params: { status, q } });
    setRows(data.alerts || []);
    setMeta({ open: data.open || 0, settings: data.settings || {},
              history: data.history || {},
              last_run_at: data.last_run_at || "" });
    onCount?.(data.open || 0);
  }, [status, q, onCount]);

  useEffect(() => { load(); }, [load]);

  const check = async () => {
    setBusy("run");
    try {
      const { data } = await api.post("/uc-cdr/alerts/run");
      toast.success(data.alerts
        ? `${data.alerts} alert(s) across ${data.checked.toLocaleString()} domains`
        : `Nothing unusual across ${data.checked.toLocaleString()} domains`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run the check");
    } finally {
      setBusy("");
    }
  };

  const setAlert = async (alert, next) => {
    setBusy(alert.alert_id);
    try {
      await api.post(`/uc-cdr/alerts/${encodeURIComponent(alert.alert_id)}/status`, null,
                     { params: { status: next } });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update that alert");
    } finally {
      setBusy("");
    }
  };

  const cfg = meta.settings || {};
  const hist = meta.history || {};
  const noBaseline = cfg.enabled !== false && hist.baseline_calls === 0;

  return (
    <div className="space-y-4" data-testid="uc-cdr-alerts">
      <div className="flex flex-wrap items-center gap-2">
        {STATES.map(([value, label]) => (
          <Button key={value} size="sm" variant={status === value ? "default" : "outline"}
                  data-testid={`uc-cdr-alert-filter-${value}`} onClick={() => setStatus(value)}>
            {label}{value === "open" && meta.open ? ` (${meta.open})` : ""}
          </Button>
        ))}
        <div className="relative ml-1">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="w-56 pl-8" placeholder="Domain or partner" value={q}
                 data-testid="uc-cdr-alert-search" onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" onClick={check} disabled={!!busy} data-testid="uc-cdr-alert-run">
            {busy === "run" ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} Check now
          </Button>
          <a href="/settings?tab=uc-cdr" data-testid="uc-cdr-alert-settings-link">
            <Button size="sm" variant="outline"><Settings2 className="h-4 w-4" /> Thresholds</Button>
          </a>
        </div>
      </div>

      <p className="max-w-3xl text-sm text-slate-500" data-testid="uc-cdr-alert-rule">
        {cfg.enabled === false
          ? "Alerts are switched off in Settings → UC CDR."
          : <>Flagging anything <strong>{cfg.multiple}x</strong> its own baseline: the last{" "}
            <strong>{cfg.window_days} days</strong> against the same stretch{" "}
            <strong>{cfg.baseline_shift_days} days earlier</strong>, ignoring domains under{" "}
            {cfg.min_minutes} billed minutes or ${cfg.min_amount} in charges.
            {meta.last_run_at ? ` Last checked ${clock(meta.last_run_at)} UTC.` : ""}</>}
      </p>

      {noBaseline && (
        <div className="max-w-3xl rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
             data-testid="uc-cdr-alert-no-baseline">
          No baseline to compare against yet — there are no stored calls between{" "}
          <span className="font-mono text-[12px]">{(hist.baseline_from || "").slice(0, 10)}</span> and{" "}
          <span className="font-mono text-[12px]">{(hist.baseline_to || "").slice(0, 10)}</span>.
          Spikes start firing once roughly <strong>{hist.days_needed} days</strong> of CDRs are in
          the table{hist.earliest_call_at
            ? <> — the oldest call stored right now is from{" "}
              <span className="font-mono text-[12px]">{hist.earliest_call_at.slice(0, 10)}</span></>
            : ""}. Until then "Check now" will always come back clean.
        </div>
      )}

      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-1.5">Domain</th>
              <th className="px-3 py-1.5">Partner</th>
              <th className="px-3 py-1.5">What</th>
              <th className="px-3 py-1.5">This window</th>
              <th className="px-3 py-1.5">Its baseline</th>
              <th className="px-3 py-1.5">Change</th>
              <th className="px-3 py-1.5">Seen</th>
              <th className="px-3 py-1.5" />
            </tr>
          </thead>
          <tbody data-testid="uc-cdr-alerts-body">
            {rows.map((a) => (
              <tr key={a.alert_id} className="border-t border-slate-100">
                <td className="px-3 py-1.5 font-medium text-slate-800">{a.domain}</td>
                <td className="px-3 py-1.5 text-slate-500">{a.territory || "—"}</td>
                <td className="px-3 py-1.5">
                  <Pill tone={a.direction === "drop" ? "bg-slate-200 text-slate-700"
                    : a.metric === "spend" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}>
                    {a.direction === "drop" ? `${a.metric} drop` : a.metric}
                  </Pill>
                </td>
                <td className="px-3 py-1.5 text-slate-800">
                  {(a.current || 0).toLocaleString()} {a.unit}
                </td>
                <td className="px-3 py-1.5 text-slate-500">
                  {(a.baseline || 0).toLocaleString()} {a.unit}
                </td>
                <td className="px-3 py-1.5 font-semibold text-slate-800">
                  {a.direction === "drop" ? `down ${a.ratio}%` : `${a.ratio}x`}
                </td>
                <td className="px-3 py-1.5 font-mono text-[11px] text-slate-500"
                    title={`${a.window_from} → ${a.window_to}`}>{(a.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-1.5 text-right">
                  {a.status === "open" ? (
                    <Button size="sm" variant="outline" disabled={busy === a.alert_id}
                            onClick={() => setAlert(a, "acked")}
                            data-testid={`uc-cdr-alert-ack-${a.domain}`}>
                      <Check className="h-4 w-4" /> Acknowledge
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" disabled={busy === a.alert_id}
                            onClick={() => setAlert(a, "open")}
                            data-testid={`uc-cdr-alert-reopen-${a.domain}`}>
                      <RotateCcw className="h-4 w-4" /> Reopen
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-slate-500"
                      data-testid="uc-cdr-alerts-empty">
                {status === "open" ? "Nothing is out of the ordinary." : "No alerts here."}
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
